import { useCallback, useEffect, useState } from 'react';
import { FileLock2, Files, LoaderCircle } from 'lucide-react';
import DocumentList from '../../components/tracking/DocumentList';
import { useI18n } from '../../contexts/I18nContext';
import { useStudentPortal } from '../../contexts/StudentPortalContext';
import trackingService from '../../services/tracking.service';
import type { DocumentChecklist } from '../../types';
import { portalApiError } from '../../utils/portalApiError';

export default function PortalDocuments() {
  const { language } = useI18n();
  const { workspace } = useStudentPortal();
  const [checklist, setChecklist] = useState<DocumentChecklist | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const application = workspace?.application;
  const applicationId = application?.id;
  const unlocked = !!workspace?.capabilities.upload_application_documents;

  const load = useCallback(async () => {
    if (!applicationId || !unlocked) return;
    setLoading(true); setError('');
    try {
      setChecklist(await trackingService.getChecklist(applicationId));
    } catch (requestError) {
      setError(portalApiError(requestError, language, { ar: 'تعذر تحميل قائمة المستندات المطلوبة.', en: 'The required document checklist could not be loaded.' }));
    } finally { setLoading(false); }
  }, [applicationId, language, unlocked]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="portal-page">
      <div className="portal-page-heading"><div><span className="portal-eyebrow"><Files size={15} />{language === 'ar' ? 'ملف الطالب' : 'Student file'}</span><h1>{language === 'ar' ? 'مستندات طلبك' : 'Application documents'}</h1><p>{language === 'ar' ? 'ارفع كل مستند مرة واحدة، وتابع مراجعة المستشار وأسباب الرفض بوضوح.' : 'Upload each document once and follow consultant review and rejection reasons clearly.'}</p></div></div>
      {!application || !unlocked ? (
        <section className="portal-card portal-empty"><div className="portal-empty-icon"><FileLock2 /></div><h3>{language === 'ar' ? 'رفع المستندات غير مفعل بعد' : 'Document upload is not active yet'}</h3><p>{language === 'ar' ? 'تظهر قائمة مستندات وجهتك هنا بعد إنشاء الطلب وتأكيد الدفعة الأولى. ستظل الصفحة متاحة لتعرف ما الخطوة التالية.' : 'Your destination checklist appears here after the application is created and the first payment is confirmed.'}</p></section>
      ) : loading ? (
        <section className="portal-card portal-empty"><LoaderCircle className="portal-spin" /></section>
      ) : error ? (
        <section className="portal-card portal-empty"><div className="portal-empty-icon"><Files /></div><h3>{error}</h3><button className="portal-button portal-button-primary" onClick={() => void load()}>{language === 'ar' ? 'إعادة المحاولة' : 'Try again'}</button></section>
      ) : checklist ? <DocumentList checklist={checklist} onUpdate={() => void load()} applicationStatus={application.status} /> : null}
    </div>
  );
}
